import type {ChronologicalCommentsSort} from '@github-ui/commenting/ChronologicalCommentsSort'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {isStaff} from '@github-ui/stats'
import {Label, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import cardAlignmentStyles from './card-alignment.module.css'
import styles from './CommentsHeader.module.css'
import {CommentsSortMenu} from './CommentsSortMenu'

const STAFF_FEEDBACK_URL = 'https://github.com/github/planning-tracking/discussions/4549'
const PREVIEW_FEEDBACK_URL = 'https://gh.io/issue-timeline-feedback'

type CommentsHeaderProps = {
  headingId: string
  sort?: ChronologicalCommentsSort
  onSortChange: (sort: ChronologicalCommentsSort) => void
  trailingControl?: ReactNode
}

export function CommentsHeader({headingId, sort, onSortChange, trailingControl}: CommentsHeaderProps) {
  const {isBrowser} = useRuntimeEnvironment()
  const staff = isBrowser && isStaff()

  return (
    <div
      data-testid={TEST_IDS.commentsHeader}
      className={clsx(
        styles.container,
        !isFeatureEnabled('issue_inline_avatars') && cardAlignmentStyles.alignWithCards,
      )}
    >
      <h2 id={headingId} className={styles.heading}>
        Comments
      </h2>
      <div className={styles.controls}>
        <a
          href={staff ? STAFF_FEEDBACK_URL : PREVIEW_FEEDBACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.previewLink}
        >
          <Label variant={staff ? 'accent' : 'success'}>{staff ? 'Staff' : 'Preview'}</Label>
          <VisuallyHidden> (give feedback on the issue timeline, opens in a new tab)</VisuallyHidden>
        </a>
        {sort && <CommentsSortMenu sort={sort} onSortChange={onSortChange} />}
        {trailingControl}
      </div>
    </div>
  )
}
